import React, { useState } from 'react';
import { FiSend, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { FaLinkedinIn, FaYoutube, FaGithub } from 'react-icons/fa';
import confetti from 'canvas-confetti';
import styles from './Contact.module.css';
import { useLanguage } from '../../context/LanguageContext';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollAnimation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<FormStatus>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        confetti({ particleCount: 200, spread: 120, origin: { y: 0.6 }, colors: ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981'], ticks: 300, zIndex: 9999 });
        setTimeout(() => confetti({ angle: 60, spread: 80, particleCount: 80, origin: { x: 0, y: 0.6 }, zIndex: 9999 }), 150);
        setTimeout(() => confetti({ angle: 120, spread: 80, particleCount: 80, origin: { x: 1, y: 0.6 }, zIndex: 9999 }), 300);
        setStatus('success');
        setForm({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 4000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  return (
    <section id="contact" className={`section ${styles.contact}`}>
      <div className="container">
        <div className={`section-header fade-in ${isVisible ? 'visible' : ''}`}>
          <span className="section-tag">{t.contact.subtitle}</span>
          <h2 className="section-title">{t.contact.title}</h2>
        </div>

        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`${styles.inner} fade-in ${isVisible ? 'visible' : ''}`}
        >
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.row}>
              <div className={styles.field}>
                <label htmlFor="name">{t.contact.nameLabel}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  disabled={status === 'sending'}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="email">{t.contact.emailLabel}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={status === 'sending'}
                />
              </div>
            </div>
            <div className={styles.field}>
              <label htmlFor="subject">{t.contact.subjectLabel}</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                required
                disabled={status === 'sending'}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="message">{t.contact.messageLabel}</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                required
                disabled={status === 'sending'}
              />
            </div>

            <button
              type="submit"
              className={`${styles.submitBtn} ${status === 'success' ? styles.success : ''} ${status === 'error' ? styles.error : ''}`}
              disabled={status === 'sending'}
            >
              {status === 'idle' && <><FiSend /> {t.contact.sendBtn}</>}
              {status === 'sending' && <>{t.contact.sending}</>}
              {status === 'success' && <><FiCheck /> {t.contact.successMsg}</>}
              {status === 'error' && <><FiAlertCircle /> {t.contact.errorMsg}</>}
            </button>
          </form>

          <div className={styles.divider} />

          <p className={styles.connectLabel}>{t.contact.connectLabel}</p>
          <div className={styles.socials}>
            <a href="https://linkedin.com/in/nuricanakturk" target="_blank" rel="noopener noreferrer" className={styles.social}>
              <FaLinkedinIn /> LinkedIn
            </a>
            <a href="https://github.com/nuricanakturk" target="_blank" rel="noopener noreferrer" className={styles.social}>
              <FaGithub /> GitHub
            </a>
            <a href="https://youtube.com/@nuricanakturk" target="_blank" rel="noopener noreferrer" className={styles.social}>
              <FaYoutube /> YouTube
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
